import React, { useCallback, useState } from "react";
import type { OptionTs } from "@ps-bridge/interop";
import { ok, type Ok } from "@ps-bridge/ok";
import { buildFeedbackSubmissionViewProps as sdeWorkbookProgramMarker } from "@ps-bridge/feedbackSubmission.program";
import * as Option from "@utils/Option";
import * as E from "fp-ts/lib/Either";
import { useSdeWorkbookClient } from "@contexts/SdeWorkbookClient.context";
import { evaluateTaskEither } from "@utils/effects/taskEitherRunner";
import { sdeWorkbookClientErrorMessage } from "@services/clients/SdeWorkbookClient";
import SdeWorkbookPagePanel from "@view/panels/sde-workbook/SdeWorkbookPage.panel";

import type { SdeWorkbookPhase } from "@view/panels/sde-workbook/SdeWorkbookUpload.panel";

const sdeWorkbookProgramName = sdeWorkbookProgramMarker.name;

const chooseMessage = "Choose the DA2024-style tax return PDF to generate an SDE workbook.";

const downloadBlob = (blob: Blob, filename: string): Ok => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
  return ok;
};

const SdeWorkbookPage: React.FC = () => {
  const sdeWorkbookClient = useSdeWorkbookClient();
  const [selectedFile, setSelectedFile] = useState<OptionTs<File>>(Option.none<File>());
  const [phase, setPhase] = useState<SdeWorkbookPhase>("idle");
  const [message, setMessage] = useState(chooseMessage);

  const onFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const firstFile = event.target.files?.item(0);
    if (firstFile == undefined) {
      setSelectedFile(Option.none<File>());
      setPhase("idle");
      setMessage(chooseMessage);
      return ok;
    }
    if (firstFile.type !== "application/pdf" && !firstFile.name.toLowerCase().endsWith(".pdf")) {
      setSelectedFile(Option.none<File>());
      setPhase("error");
      setMessage("That file is not a PDF. Choose the DA2024 tax return PDF.");
      event.target.value = "";
      return ok;
    }
    setSelectedFile(Option.some(firstFile));
    setPhase("ready");
    setMessage(`${firstFile.name} is ready for SDE workbook generation.`);
    return ok;
  }, []);

  const onSubmit = useCallback(async (): Promise<Ok> => {
    return Option.fold(
      selectedFile,
      async () => ok,
      async (file) => {
        if (phase === "submitting") {
          return ok;
        }
        setPhase("submitting");
        setMessage("Generating SDE workbook from tax-return evidence...");
        const result = await evaluateTaskEither(sdeWorkbookClient.generateWorkbook(file));
        if (E.isLeft(result)) {
          setPhase("error");
          setMessage(sdeWorkbookClientErrorMessage(result.left));
          return ok;
        }
        downloadBlob(result.right, "da2024-sde-workbook.xlsx");
        setPhase("success");
        setMessage("SDE workbook downloaded. Add-backs are auto-suggested and still require broker review.");
        return ok;
      },
    );
  }, [phase, sdeWorkbookClient, selectedFile]);

  return (
    <SdeWorkbookPagePanel
      programName={sdeWorkbookProgramName}
      phase={phase}
      selectedFile={selectedFile}
      message={message}
      onFileChange={onFileChange}
      onSubmit={onSubmit}
    />
  );
};

export default SdeWorkbookPage;
